
import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import "../Style/ClientUserManagement.css";

const createAPI = () => {
  const token = localStorage.getItem("token");
  return axios.create({
    baseURL: "https://api.yellowmatrix.co.in",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

const SuccessNotification = ({ message, onClose }) => (
  <div className="success-notification">
    <span>{message}</span>
    <button onClick={onClose} className="close-notification">
      ×
    </button>
  </div>
);

const ClientUserManagement = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserCustomerID, setCurrentUserCustomerID] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    userId: null,
    userName: ""
  });
  const [error, setError] = useState(null);
  
  const [newUser, setNewUser] = useState({
    Username: "",
    Email: "",
    Password: "",
    Role: "User",
    CustomerID: "",
    Status: true,
  });

  const successTimeoutRef = useRef(null);

  // Fetch users query
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const API = createAPI();
        const response = await API.get("/users");
        if (response.data && response.data.Users) {
          return response.data.Users;
        }
        throw new Error("Invalid response structure");
      } catch (error) {
        setError(error.response?.data?.error || "Failed to fetch users. Please check your permissions.");
        throw error;
      }
    },
    onError: (error) => {
      console.error("Error fetching users:", error);
    },
    initialData: [],
  });

  // Mutations
  const addUserMutation = useMutation({
    mutationFn: async (payload) => {
      const API = createAPI();
      return await API.post("/users", payload);
    },
    onSuccess: () => {
      showSuccess(editMode ? "User updated successfully" : "User created successfully");
      queryClient.invalidateQueries(['users']);
      resetForm();
    },
    onError: (error) => {
      console.error("Error saving user:", error);
      setError(error.response?.data?.error || "Failed to save user");
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: async (payload) => {
      const API = createAPI();
      return await API.patch("/users", payload);
    },
    onSuccess: () => {
      showSuccess("User updated successfully");
      queryClient.invalidateQueries(['users']);
      resetForm();
    },
    onError: (error) => {
      console.error("Error updating user:", error);
      setError(error.response?.data?.error || "Failed to update user");
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      const API = createAPI();
      return await API.delete("/users", {
        data: { UserID: userId }
      });
    },
    onSuccess: () => {
      showSuccess("User deleted successfully");
      queryClient.invalidateQueries(['users']);
      setDeleteConfirmation({
        isOpen: false,
        userId: null,
        userName: ""
      });
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
      setError(error.response?.data?.error || "Failed to delete user");
    }
  });

  // Initialize user role and Customer ID
  useEffect(() => {
    const role = localStorage.getItem("userRole");
    const CustomerID = localStorage.getItem("CustomerID");
    setCurrentUserRole(role);
    setCurrentUserCustomerID(CustomerID);
  }, []);

  const showSuccess = (message) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setSuccessMessage(message);
    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  const validateForm = () => {
    if (!newUser.Username || !newUser.Email) {
      setError("Username and Email are required");
      return false;
    }
    if (!editMode && !newUser.Password) {
      setError("Password is required for new users");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(newUser.Email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (currentUserRole === "Admin" && !newUser.CustomerID) {
      setError("Customer ID is required for Admin");
      return false;
    }
    return true;
  };

  const handleAddUser = async () => {
    if (!validateForm()) return;
    
    const payload = {
      Email: newUser.Email,
      Username: newUser.Username,
      Role: newUser.Role,
      Status: newUser.Status,
      CustomerID: currentUserRole === "Admin" ? newUser.CustomerID : currentUserCustomerID
    };

    if (newUser.Password) {
      payload.Password = newUser.Password;
    }

    if (editMode) {
      payload.UserID = newUser.UserID;
      await updateUserMutation.mutateAsync(payload);
    } else {
      await addUserMutation.mutateAsync(payload);
    }
  };

  const resetForm = () => {
    setModalOpen(false);
    setEditMode(false);
    setNewUser({
      Username: "",
      Email: "",
      Password: "",
      Role: "User",
      CustomerID: "",
      Status: true,
    });
    setShowPassword(false);
    setError(null);
  };

  const handleEditClick = (user) => {
    setNewUser({
      ...user,
      Password: "",
    });
    setEditMode(true);
    setModalOpen(true);
    setError(null);
  };

  const handleDeleteUser = async () => {
    await deleteUserMutation.mutateAsync(deleteConfirmation.userId);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.Username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "All Roles" || user.Role === roleFilter;

    const matchesStatus =
      statusFilter === "All Status" ||
      (statusFilter === "Active" && user.Status) ||
      (statusFilter === "Inactive" && !user.Status);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB") + ", " + 
           date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  const isLoadingAnyMutation = addUserMutation.isLoading || updateUserMutation.isLoading || deleteUserMutation.isLoading;

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1 className="user-management-title">User Management</h1>
        <hr className="line" width="100%" />
        <p className="user-management-subtitle">
          {currentUserRole === "Admin" 
            ? "Manage all organization users" 
            : "Manage client users"}
        </p>
      </div>

      <div className="filter-controls">
        <div className="search-container-user">
          <i className="fa-solid fa-magnifying-glass search-icon-user"></i>
        <input
          type="text"
          placeholder="Search users..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        </div>
        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="All Roles">All Roles</option>
          <option value="Admin">Admin</option>
          <option value="User">User</option>
          {currentUserRole === "Admin" && <option value="Admin">Admin</option>}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All Status">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button 
          className="add-user-btn" 
          onClick={() => {
            setEditMode(false);
            setModalOpen(true);
          }}
          disabled={isLoadingAnyMutation}
        >
          Add New User
        </button>
      </div>

      {error && <div className="error-message-client">{error}</div>}

      {successMessage && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            if (successTimeoutRef.current) {
              clearTimeout(successTimeoutRef.current);
            }
            setSuccessMessage(null);
          }}
        />
      )}

      {isLoading ? (
        <div className="loading-state">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="no-users-message">
          {searchTerm || roleFilter !== "All Roles" || statusFilter !== "All Status"
            ? "No users match your search criteria"
            : "No users found"}
        </div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              {currentUserRole === "Admin" && <th>Customer ID</th>}
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.UserID}>
                <td>{user.Username}</td>
                <td>{user.Email}</td>
                <td>{user.Role}</td>
                {currentUserRole === "Admin" && <td>{user.CustomerID || "—"}</td>}
                <td>
                  <span className={`status-badge ${user.Status ? "status-active" : "status-inactive"}`}>
                    {user.Status ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{formatDate(user.LastLogin)}</td>
                <td>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditClick(user)}
                    disabled={isLoadingAnyMutation}
                  >
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => setDeleteConfirmation({
                      isOpen: true,
                      userId: user.UserID,
                      userName: user.Username
                    })}
                    disabled={isLoadingAnyMutation}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit User Modal */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {editMode ? "Edit User" : "Add New User"}
              </h3>
            </div>
            <div className="form-group">
              <label className="form-label">Name*</label>
              <input
                placeholder="Name"
                className="form-input"
                value={newUser.Username}
                onChange={(e) => setNewUser({...newUser, Username: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email*</label>
              <input
                placeholder="Email"
                type="email"
                className="form-input"
                value={newUser.Email}
                onChange={(e) => setNewUser({...newUser, Email: e.target.value})}
                required
              />
            </div>

            {(!editMode || (editMode && currentUserRole === "Admin")) && (
              <div className="form-group">
                <label className="form-label">
                  {editMode ? "New Password (leave blank to keep current)" : "Password*"}
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={editMode ? "New password" : "Create password"}
                    className="form-input"
                    value={newUser.Password}
                    onChange={(e) => setNewUser({...newUser, Password: e.target.value})}
                    required={!editMode}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {!editMode && (
                  <div className="password-hints">
                    Password must contain at least 8 characters, including
                    uppercase, lowercase, number, and special character.
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Role*</label>
              <select
                className="form-select"
                value={newUser.Role}
                onChange={(e) => setNewUser({...newUser, Role: e.target.value})}
                required
              >
                <option value="User">User</option>
                <option value="Admin">Admin</option>
                {currentUserRole === "Admin" && (
                  <option value="Admin">Admin</option>
                )}
              </select>
            </div>

            {currentUserRole === "Admin" && (
              <div className="form-group">
                <label className="form-label">Customer ID*</label>
                <input
                  placeholder="Customer ID"
                  className="form-input"
                  value={newUser.CustomerID}
                  onChange={(e) => setNewUser({...newUser, CustomerID: e.target.value})}
                  required={currentUserRole === "Admin"}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={newUser.Status}
                  onChange={(e) => setNewUser({...newUser, Status: e.target.checked})}
                />
                Active
              </label>
            </div>
            <div className="modal-footer">
              <button onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button 
                onClick={handleAddUser} 
                className="submit-btn"
                disabled={isLoadingAnyMutation}
              >
                {isLoadingAnyMutation ? "Processing..." : (editMode ? "Update User" : "Add User")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3 className="modal-title">Confirm Deletion</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete user <strong>{deleteConfirmation.userName}</strong>?</p>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => setDeleteConfirmation({
                  isOpen: false,
                  userId: null,
                  userName: ""
                })} 
                className="cancel-btn"
                disabled={isLoadingAnyMutation}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteUser} 
                className="delete-confirm-btn"
                disabled={isLoadingAnyMutation}
              >
                {deleteUserMutation.isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUserManagement;